import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { fetchWithAuth } from '../utils/api';
import '../styles/GitHubPushChanges.css';

function GitHubPushChanges({ problemId, onPushComplete }) {
  const [fileChanges, setFileChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChanges, setSelectedChanges] = useState([]);
  const [commitMessage, setCommitMessage] = useState('');
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [repoInfo, setRepoInfo] = useState(null);
  const [comparison, setComparison] = useState(null);

  const fetchFileChanges = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch unpushed file changes
      const changesResponse = await fetchWithAuth(
        `${API_BASE_URL}/github/problems/${problemId}/changes?pushed=false`
      );

      if (!changesResponse.ok) {
        throw new Error('Failed to fetch file changes');
      }

      const changesData = await changesResponse.json();
      setFileChanges(changesData.file_changes || []);
      setSelectedChanges(changesData.file_changes?.map(fc => fc.id) || []);

      // Fetch repo info and comparison
      const repoResponse = await fetchWithAuth(
        `${API_BASE_URL}/github/problems/${problemId}/repo`
      );

      if (repoResponse.ok) {
        const repoData = await repoResponse.json();
        setRepoInfo(repoData.repository);
        setComparison(repoData.comparison);
      }
    } catch (err) {
      console.error('Error fetching file changes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [problemId]);

  useEffect(() => {
    if (problemId) {
      fetchFileChanges();
    }
  }, [problemId, fetchFileChanges]);

  const toggleChangeSelection = (changeId) => {
    setSelectedChanges(prev => {
      if (prev.includes(changeId)) {
        return prev.filter(id => id !== changeId);
      }
      return [...prev, changeId];
    });
  };

  const selectAllChanges = () => {
    setSelectedChanges(fileChanges.map(fc => fc.id));
  };

  const deselectAllChanges = () => {
    setSelectedChanges([]);
  };

  const handlePush = async () => {
    if (selectedChanges.length === 0) {
      alert('Please select at least one file change to push');
      return;
    }

    if (!commitMessage.trim()) {
      alert('Please enter a commit message');
      return;
    }

    try {
      setPushing(true);
      setError(null);
      setPushResult(null);

      const response = await fetchWithAuth(
        `${API_BASE_URL}/github/problems/${problemId}/push`,
        {
          method: 'POST',
          body: JSON.stringify({
            message: commitMessage,
            file_change_ids: selectedChanges
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to push changes');
      }

      const result = await response.json();
      setPushResult(result);
      setCommitMessage('');

      // Refresh file changes
      await fetchFileChanges();

      if (onPushComplete) {
        onPushComplete(result);
      }
    } catch (err) {
      console.error('Error pushing changes:', err);
      setError(err.message);
    } finally {
      setPushing(false);
    }
  };

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'created':
        return { icon: '+', color: '#10b981', label: 'Added' };
      case 'modified':
        return { icon: '~', color: '#f59e0b', label: 'Modified' };
      case 'deleted':
        return { icon: '-', color: '#ef4444', label: 'Deleted' };
      default:
        return { icon: '?', color: '#6b7280', label: 'Unknown' };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="github-push-changes loading">
        <div className="loading-spinner"></div>
        <p>Loading file changes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="github-push-changes error">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchFileChanges}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="github-push-changes">
      {/* Repository Info */}
      {repoInfo && (
        <div className="repo-info-section">
          <div className="repo-header">
            <span className="repo-icon">📦</span>
            <span className="repo-name">{repoInfo.owner}/{repoInfo.name}</span>
          </div>
          <div className="branch-info">
            <div className="branch-item">
              <span className="branch-label">Default Branch:</span>
              <span className="branch-name">{repoInfo.default_branch}</span>
            </div>
            <div className="branch-item">
              <span className="branch-label">Working Branch:</span>
              <span className="branch-name working">{repoInfo.working_branch}</span>
            </div>
          </div>
          {comparison && (
            <div className="comparison-info">
              <span className="comparison-status">
                {comparison.ahead_by > 0 && (
                  <span className="ahead">↑ {comparison.ahead_by} commit{comparison.ahead_by !== 1 ? 's' : ''} ahead</span>
                )}
                {comparison.behind_by > 0 && (
                  <span className="behind">↓ {comparison.behind_by} commit{comparison.behind_by !== 1 ? 's' : ''} behind</span>
                )}
                {comparison.status === 'identical' && (
                  <span className="identical">Branches are identical</span>
                )}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Push Result */}
      {pushResult && (
        <div className="push-result success">
          <div className="result-header">
            <span className="success-icon">✓</span>
            <span>Changes pushed successfully!</span>
          </div>
          <div className="result-details">
            <p>Commit: <code>{pushResult.commit.sha.substring(0, 7)}</code></p>
            <p>Branch: {pushResult.branch}</p>
            <p>Files pushed: {pushResult.files_pushed}</p>
          </div>
        </div>
      )}

      {/* File Changes List */}
      <div className="changes-section">
        <div className="changes-header">
          <h3>Pending Changes ({fileChanges.length})</h3>
          {fileChanges.length > 0 && (
            <div className="selection-controls">
              <button onClick={selectAllChanges}>Select All</button>
              <button onClick={deselectAllChanges}>Deselect All</button>
            </div>
          )}
        </div>

        {fileChanges.length === 0 ? (
          <div className="no-changes">
            <span className="no-changes-icon">📭</span>
            <p>No pending changes to push</p>
            <p className="hint">Changes made by the agent will appear here</p>
          </div>
        ) : (
          <div className="changes-list">
            {fileChanges.map((change) => {
              const typeInfo = getChangeTypeIcon(change.change_type);
              const isSelected = selectedChanges.includes(change.id);

              return (
                <div
                  key={change.id}
                  className={`change-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleChangeSelection(change.id)}
                >
                  <div className="change-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleChangeSelection(change.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="change-type" style={{ color: typeInfo.color }}>
                    <span className="type-icon">{typeInfo.icon}</span>
                    <span className="type-label">{typeInfo.label}</span>
                  </div>
                  <div className="change-path">
                    <span className="file-path">{change.file_path}</span>
                  </div>
                  <div className="change-date">
                    {formatDate(change.updated_at)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Commit and Push Section */}
      {fileChanges.length > 0 && (
        <div className="commit-section">
          <div className="commit-message-input">
            <label htmlFor="commit-message">Commit Message</label>
            <textarea
              id="commit-message"
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              placeholder="Describe your changes..."
              rows={3}
              disabled={pushing}
            />
          </div>

          <div className="push-actions">
            <div className="selected-count">
              {selectedChanges.length} of {fileChanges.length} changes selected
            </div>
            <button
              className="push-btn"
              onClick={handlePush}
              disabled={pushing || selectedChanges.length === 0 || !commitMessage.trim()}
            >
              {pushing ? (
                <>
                  <span className="loading-spinner small"></span>
                  Pushing...
                </>
              ) : (
                <>
                  <span className="push-icon">⬆️</span>
                  Push to {repoInfo?.working_branch || 'GitHub'}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default GitHubPushChanges;
